import React from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import type { Pickup, PickupStatus } from "../../types/pickup";

type DriverPickupsListProps = {
  pickups: Pickup[];
};

const cardStyle: React.CSSProperties = {
  borderTop: "3px solid var(--primary)",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "16px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginTop: "4px",
};

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

const itemStyle = (status: PickupStatus): React.CSSProperties => ({
  border: "1px solid var(--border)",
  borderLeft:
    status === "completed"
      ? "4px solid var(--success)"
      : status === "incident"
        ? "4px solid var(--danger)"
        : status === "in_progress"
          ? "4px solid var(--warning)"
          : "4px solid var(--primary)",
  borderRadius: "20px",
  background:
    status === "in_progress"
      ? "linear-gradient(180deg, var(--primary-soft) 0%, var(--surface-alt) 100%)"
      : "var(--surface-alt)",
  padding: "16px",
});

const topStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "14px",
  marginBottom: "12px",
};

const storeNameStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const addressStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginTop: "4px",
  lineHeight: 1.5,
};

const badgeStyle = (status: PickupStatus): React.CSSProperties => ({
  minHeight: "34px",
  padding: "0 12px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  display: "inline-flex",
  alignItems: "center",
  fontSize: "12px",
  fontWeight: 700,
  whiteSpace: "nowrap",
  background:
    status === "completed"
      ? "rgba(0,0,0,0.04)"
      : status === "incident"
        ? "rgba(0,0,0,0.04)"
        : status === "in_progress"
          ? "var(--primary-soft)"
          : "var(--surface)",
  color:
    status === "completed"
      ? "var(--success)"
      : status === "incident"
        ? "var(--danger)"
        : status === "in_progress"
          ? "var(--warning)"
          : "var(--text-primary)",
});

const detailsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
};

const detailCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "var(--surface)",
  padding: "12px 14px",
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginBottom: "6px",
};

const detailValueStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const notesStyle: React.CSSProperties = {
  marginTop: "12px",
  fontSize: "13px",
  color: "var(--text-secondary)",
  lineHeight: 1.5,
};

const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  marginTop: "14px",
  flexWrap: "wrap",
};

const emptyStateStyle: React.CSSProperties = {
  minHeight: "180px",
  borderRadius: "18px",
  border: "1px dashed var(--border)",
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "24px",
  textAlign: "center",
};

const emptyStateTitleStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const emptyStateTextStyle: React.CSSProperties = {
  maxWidth: "460px",
  fontSize: "13px",
  lineHeight: 1.55,
  color: "var(--text-secondary)",
};

const getStatusLabel = (status: PickupStatus): string => {
  switch (status) {
    case "assigned":
      return "Asignada";
    case "accepted":
      return "Aceptada";
    case "in_progress":
      return "En curso";
    case "completed":
      return "Finalizada";
    case "incident":
      return "Con incidencia";
    default:
      return "Sin estado";
  }
};

const DriverPickupsList = ({ pickups }: DriverPickupsListProps) => {
  return (
    <Card style={cardStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={titleStyle}>Próximas colectas</div>
          <div style={subtitleStyle}>
            Seguimiento de las colectas asignadas al chofer
          </div>
        </div>

        {pickups.length > 0 ? (
          <Button variant="secondary">Ver todas</Button>
        ) : null}
      </div>

      {pickups.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={emptyStateTitleStyle}>No hay colectas disponibles</div>

          <div style={emptyStateTextStyle}>
            Cuando las colectas estén vinculadas al sistema, vas a poder consultar
            acá sus horarios, comercios, cantidades y estados.
          </div>
        </div>
      ) : (
        <div style={listStyle}>
          {pickups.map((pickup) => (
            <div key={pickup.id} style={itemStyle(pickup.status)}>
              <div style={topStyle}>
                <div>
                  <div style={storeNameStyle}>{pickup.storeName}</div>
                  <div style={addressStyle}>{pickup.address}</div>
                </div>

                <div style={badgeStyle(pickup.status)}>
                  {getStatusLabel(pickup.status)}
                </div>
              </div>

              <div style={detailsGridStyle}>
                <div style={detailCardStyle}>
                  <div style={detailLabelStyle}>Horario</div>
                  <div style={detailValueStyle}>{pickup.scheduleLabel}</div>
                </div>

                <div style={detailCardStyle}>
                  <div style={detailLabelStyle}>Esperados</div>
                  <div style={detailValueStyle}>{pickup.quantityExpected}</div>
                </div>

                <div style={detailCardStyle}>
                  <div style={detailLabelStyle}>Colectados</div>
                  <div style={detailValueStyle}>{pickup.quantityCollected}</div>
                </div>
              </div>

              {pickup.notes ? <div style={notesStyle}>{pickup.notes}</div> : null}

              <div style={actionsRowStyle}>
                <Button variant="primary">Ver detalle</Button>
                <Button variant="secondary">Reportar incidencia</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default DriverPickupsList;